import { ErrorHandler, Injectable, inject } from '@angular/core';
import { ToastService } from '../../services/toast.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private toastService = inject(ToastService);

  handleError(error: any): void {
    console.error('Global Error Handler:', error);

    let userMessage = 'An unexpected error occurred';
    
    // Handle different types of errors
    if (error?.rejection) {
      // Handle promise rejections
      userMessage = this.getErrorMessage(error.rejection);
    } else if (error?.error) {
      // Handle HTTP errors
      userMessage = this.getErrorMessage(error.error);
    } else if (error?.message) {
      // Handle standard errors
      userMessage = this.getUserFriendlyMessage(error.message);
    }

    // Show error to user
    this.toastService.error(userMessage, true);

    // In production, you might want to send errors to a logging service
    // this.logError(error);
  }

  private getErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return this.getUserFriendlyMessage(error);
    }

    if (error?.message) {
      return this.getUserFriendlyMessage(error.message);
    }

    if (error?.error?.message) {
      return this.getUserFriendlyMessage(error.error.message);
    }

    return 'An unexpected error occurred';
  }

  private getUserFriendlyMessage(message: string): string {
    // Convert technical error messages to user-friendly ones
    const errorMappings: Record<string, string> = {
      'Network Error': 'Please check your internet connection and try again',
      'Failed to fetch': 'Unable to connect to the server. Please try again later',
      'Unauthorized': 'Your session has expired. Please log in again',
      'Forbidden': 'You do not have permission to perform this action',
      'Not Found': 'The requested resource was not found',
      'Internal Server Error': 'Server error occurred. Please try again later',
      'Bad Gateway': 'Server is temporarily unavailable. Please try again later',
      'Service Unavailable': 'Service is temporarily unavailable. Please try again later',
      'Timeout': 'Request timed out. Please try again',
      'ChunkLoadError': 'Failed to load application resources. Please refresh the page'
    };

    // Check for exact matches
    for (const [technical, friendly] of Object.entries(errorMappings)) {
      if (message.toLowerCase().includes(technical.toLowerCase())) {
        return friendly;
      }
    }

    // For Firebase Auth errors (if not already handled in AuthService)
    if (message.includes('auth/')) {
      return 'Authentication error occurred. Please try again';
    }

    // For other technical messages, provide a generic friendly message
    if (message.length > 100 || message.includes('Error:') || message.includes('Exception:')) {
      return 'An unexpected error occurred. Please try again or contact support if the problem persists';
    }

    return message;
  }

  // Optional: Method to send errors to a logging service
  // private logError(error: any): void {
  //   // Send to logging service like Sentry, LogRocket, etc.
  // }
}